import { RequestHeaderKeyTypes, RequestHeaderValueTypes } from 'constants/RequestHeaderTypes';
import { clearObject } from 'helpers/objects';
import { EFileTypes, queryString, queryStringWithSort } from 'helpers/request';
import { isResponseError, TResponse } from 'models/apiClient';
import { ApiEndpoints } from 'models/apiEndpoints';
import {
  ILicenceAuthority,
  ILicenceAuthorityFormValues,
  ILicenceAuthorityPrintoutErrorLog,
  ILicenceAuthorityPrintoutErrorLogResponse,
  ILicenceAuthorityPrintoutSettings,
  ILicenceAuthorityPrintoutSettingsFormValues,
  ILicenceAuthorityResponse,
  ILicenceAuthoritySendState,
  ILicenceAuthoritySftpAlertStatus,
} from 'models/licenceAuthority';
import { EPerPages, ISortOptions } from 'models/table';
import { apiClient } from 'utils/apiClient';
import { removeFileNameSpecialCharacters } from 'utils/files';

const getlicenceAuthorities = async (
  page: number,
  size: number,
  sort: ISortOptions | null | undefined,
  query: Partial<any>,
) => {
  const qString = queryStringWithSort(
    {
      page: page - 1,
      size,
    },
    sort,
  );

  const { data } = await apiClient.post<TResponse<ILicenceAuthorityResponse>>(
    `${ApiEndpoints.GET_LICENSES_AUTHORITIES()}?${qString}`,
    clearObject(query),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getLicenceAuthority = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ILicenceAuthority>>(
    `${ApiEndpoints.GET_LICENSES_AUTHORITY(id)}`,
  );
  if (isResponseError(data)) {
    return null;
  }

  return data;
};
const getLicenceAuthoritySftpSendState = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ILicenceAuthoritySendState>>(
    `${ApiEndpoints.GET_LICENSES_AUTHORITY_SFTP_SEND_STATE(id)}`,
  );
  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const createLicenceAuthority = async (requestData: ILicenceAuthorityFormValues) => {
  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const { signatureAttachmentFile, ...request } = requestData;

  const formData = new FormData();
  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: EFileTypes.APPLICATION_JSON,
    }),
  );

  if (signatureAttachmentFile) {
    const name = signatureAttachmentFile.name;
    formData.append('file', signatureAttachmentFile, name);
  }

  const data = await apiClient.post<TResponse<any>>(
    ApiEndpoints.CREATE_LICENSES_AUTHORITY(),
    formData,
    config,
  );

  return data;
};

const updateLicenceAuthority = async (
  id: string,
  requestData: ILicenceAuthorityFormValues,
) => {
  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const { signatureAttachmentFile, ...request } = requestData;

  const formData = new FormData();
  formData.append(
    'request',
    new Blob([JSON.stringify(request)], {
      type: EFileTypes.APPLICATION_JSON,
    }),
  );

  if (signatureAttachmentFile) {
    const name = removeFileNameSpecialCharacters(signatureAttachmentFile.name);
    formData.append('file', signatureAttachmentFile, name);
  }

  const data = await apiClient.put<TResponse<any>>(
    ApiEndpoints.UPDATE_LICENSES_AUTHORITY(id),
    formData,
    config,
  );

  return data;
};

const createLicenceAuthorityPrintoutSettings = async (id: string) => {
  const { data } = await apiClient.post<TResponse<any>>(
    ApiEndpoints.CREATE_LICENSES_PRINTOUT_SETTINGS(id),
    {},
    {
      timeout: 60000,
    },
  );
  return data;
};
const updateLicenceAuthorityPrintoutSettings = async (
  id: string,
  version: number,
  body: ILicenceAuthorityPrintoutSettingsFormValues,
) => {
  const data = await apiClient.put<TResponse<any>>(
    `${ApiEndpoints.UPDATE_LICENSES_PRINTOUT_SETTINGS(id)}?version=${version}`,
    body,
  );
  return data;
};
const deleteLicenceAuthorityPrintoutSettings = async (id: string, version: number) => {
  const { data } = await apiClient.delete<TResponse<any>>(
    `${ApiEndpoints.DELETE_LICENSES_PRINTOUT_SETTINGS(id)}?version=${version}`,
  );
  return !isResponseError(data);
};
const rotatePGPKeyLicenceAuthorityPrintoutSettings = async (id: string, version: number) => {
  const { data } = await apiClient.post<TResponse<any>>(
    `${ApiEndpoints.ROTATE_PGP_KEY_LICENSES_PRINTOUT_SETTINGS(id)}?version=${version}`,
    {},
    {
      timeout: 60000,
    },
  );
  return data;
};

const getLicenceAuthorityPrintoutErrorLogData = async (
  id: string,
  currentPage: number,
  perPage: EPerPages,
) => {
  const qString = queryString({
    page: currentPage - 1,
    size: perPage,
  });

  const { data } = await apiClient.get<TResponse<ILicenceAuthorityPrintoutErrorLogResponse>>(
    `${ApiEndpoints.GET_LICENSE_AUTHORITY_PRINTOUT_ERROR_LOG_DATA(id)}?${qString}`,
  );
  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getLicenceAuthoritySftpAlertStatus = async (id: string) => {
  const { data } = await apiClient.get<TResponse<ILicenceAuthoritySftpAlertStatus>>(
    ApiEndpoints.GET_LICENCE_AUTHORITY_SFTP_ALERT_STATUS(id),
  );
  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const resolveLicenceAuthorityPrintoutErrorLogData = async (id: string, alertId: string) => {
  const data = await apiClient.post<TResponse<any>>(
    `${ApiEndpoints.RESOLVE_LICENSE_AUTHORITY_PRINTOUT_ERROR_LOG_DATA(id, alertId)}`,
  );
  return data;
};

const rotateSSHKeyLicenceAuthorityPrintoutSettings = async (id: string, version: number) => {
  const { data } = await apiClient.post<TResponse<any>>(
    `${ApiEndpoints.ROTATE_SSH_KEY_LICENSES_PRINTOUT_SETTINGS(id)}?version=${version}`,
  );
  return data;
};
const resumeSendingLicenceAuthorityPrintoutSettings = async (id: string) => {
  const { data } = await apiClient.post<TResponse<any>>(
    ApiEndpoints.RESUME_SENDING_LICENCE_AUTHORITY_PRINTOUT_SETTINGS(id),
  );
  return data;
};
const readLicenceAuthorityDeletionInfo = async (id: string) => {
  const { data } = await apiClient.get<TResponse<any>>(
    ApiEndpoints.READ_LICENSES_PRINTOUT_DELETION_INFO(id),
  );
  return data;
};
const getLicenceAuthorityPrintoutSettings = async (id: string) => {
  const { data } = await apiClient.get<TResponse<any>>(
    ApiEndpoints.GET_LICENSES_PRINTOUT_SETTINGS(id),
  );
  return data;
};
const getLicenceAuthoritySignatureValidate = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const config = {
    headers: {
      [RequestHeaderKeyTypes.CONTENT_TYPE]: RequestHeaderValueTypes.MULTIPART_FORM_DATA,
    },
    timeout: 0,
  };

  const { data } = await apiClient.post<TResponse<{ warnings: string[] }>>(
    ApiEndpoints.VALIDATE_LICENSES_AUTHORITY_SIGNATUE(),
    formData,
    config,
  );
  if (isResponseError(data)) {
    return null;
  }

  return data;
};

export {
  getlicenceAuthorities,
  getLicenceAuthority,
  createLicenceAuthority,
  updateLicenceAuthority,
  getLicenceAuthorityPrintoutSettings,
  createLicenceAuthorityPrintoutSettings,
  getLicenceAuthoritySignatureValidate,
  updateLicenceAuthorityPrintoutSettings,
  deleteLicenceAuthorityPrintoutSettings,
  readLicenceAuthorityDeletionInfo,
  getLicenceAuthorityPrintoutErrorLogData,
  getLicenceAuthoritySftpAlertStatus,
  rotatePGPKeyLicenceAuthorityPrintoutSettings,
  rotateSSHKeyLicenceAuthorityPrintoutSettings,
  resumeSendingLicenceAuthorityPrintoutSettings,
  resolveLicenceAuthorityPrintoutErrorLogData,
  getLicenceAuthoritySftpSendState,
};
